import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import Seo from "@/seo/Seo";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const { verifyEmail } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "verifying" : "no-token"); // verifying | ok | error | no-token
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current || !token) return;
    ran.current = true;
    (async () => {
      const res = await verifyEmail(token);
      setStatus(res.ok ? "ok" : "error");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Seo title="Verify email — FloorMatch" description="Confirm your FloorMatch email address." path="/verify-email" />
      <PublicHeader />
      <main className="min-h-screen bg-slate-50 py-10" data-testid="verify-email-page">
        <div className="max-w-md mx-auto px-4 sm:px-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center">
            {status === "verifying" && (
              <div data-testid="verify-loading">
                <Loader2 className="w-10 h-10 text-[#E05915] mx-auto animate-spin" />
                <h1 className="font-display font-bold text-xl text-slate-900 mt-3">Confirming your email…</h1>
              </div>
            )}
            {status === "ok" && (
              <div data-testid="verify-success">
                <CheckCircle2 className="w-10 h-10 text-[#2A5A3B] mx-auto" />
                <h1 className="font-display font-bold text-xl text-slate-900 mt-3">Email confirmed</h1>
                <p className="text-sm text-slate-500 mt-1">Thanks — your email address is now verified.</p>
                <Link to="/account" className="mt-5 inline-flex items-center justify-center h-11 px-5 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold" data-testid="verify-go-account">
                  Go to my account
                </Link>
              </div>
            )}
            {(status === "error" || status === "no-token") && (
              <div data-testid="verify-error">
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
                <h1 className="font-display font-bold text-xl text-slate-900 mt-3">Link invalid or expired</h1>
                <p className="text-sm text-slate-500 mt-1">
                  This verification link is no longer valid. Sign in and we'll send you a fresh one.
                </p>
                <Link to="/account" className="mt-5 inline-flex items-center justify-center h-11 px-5 rounded-full bg-[#0F3D2E] text-white font-semibold" data-testid="verify-go-signin">
                  Go to my account
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
